import {
  Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, Menu, MenuItem,
  Stack, TextField, Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import RichContentViewer from "../../../Shared/RichContentViewer";
import SendMessage from "./SendMessage";
import { ProjectContext } from "../../../AppContext/ProjectContext";
import { Timestamp } from "firebase/firestore";
import { AuthContext } from "../../../AppContext/AuthContext";
import {
  getAnswersInRealTimeById,
  updateAnswer,
  deleteAnswer,
} from "../../../Config/db/answers.retrieve";
import { extractPlainText } from "../helper";
import { MoreVert, Edit, Delete } from "@mui/icons-material";
import moment from "moment";

export default function Answers({ questionId, handleReply, replyTriggerId }) {
  const [messages, setMessages] = useState([]);
  const { participants } = useContext(ProjectContext);
  const { currentUser } = useContext(AuthContext);
  const [showAnswers, setShowAnswers] = useState(false);
  const replyRef = React.useRef();

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItemId, setMenuItemId] = useState(null);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  useEffect(() => {
    if (questionId) {
      const unSub = getAnswersInRealTimeById(questionId, (data) => {
        setMessages(data);
      });
      return () => {
        unSub();
      };
    }
  }, [questionId]);

  const accordionAction = () => {
    setShowAnswers(!showAnswers);
  };

  const handleMenuOpen = (e, id) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuItemId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuItemId(null);
  };

  const handleEditStart = (item) => {
    setEditingId(item.id);
    setEditText(extractPlainText(item.text));
    handleMenuClose();
  };

  const handleEditSave = async () => {
    if (editingId && editText.trim()) {
      await updateAnswer(editingId, editText.trim());
    }
    setEditingId(null);
    setEditText("");
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditText("");
  };

  const handleDeleteClick = (id) => {
    setDeleteTargetId(id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (deleteTargetId) {
      await deleteAnswer(deleteTargetId);
    }
    setDeleteDialogOpen(false);
    setDeleteTargetId(null);
  };

  if (showAnswers) {
    return (
      <Stack spacing={0} sx={{ mt: 0.5 }}>
        <Box
          onClick={accordionAction}
          sx={{ cursor: "pointer", display: "inline-flex", mb: 0.5 }}
        >
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`${messages.length} repl${messages.length > 1 ? "ies" : "y"}`}
            sx={{ fontWeight: 600, fontSize: "0.7rem", height: 22 }}
          />
        </Box>
        {messages?.map((each) => {
          const date = new Timestamp(
            each.timestamp.seconds,
            each.timestamp.nanoseconds
          ).toDate();
          const isOwner = each.senderId === currentUser.uid;
          const authorName = isOwner
            ? currentUser.displayName
            : participants?.[each.senderId]?.displayName || "Unknown";
          const photoURL = isOwner
            ? currentUser.photoURL
            : participants?.[each.senderId]?.photoURL;

          return (
            <Box key={each.id} sx={{ py: 0.75, "&:not(:last-child)": { borderBottom: "1px solid #f5f5f5" } }}>
              {/* Header */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
                <Avatar src={photoURL} sx={{ width: 20, height: 20, fontSize: "0.6rem" }}>
                  {authorName?.[0] || "?"}
                </Avatar>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.78rem" }}>
                  {authorName}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }}>
                  {moment(date).fromNow()}
                </Typography>
                {each.editedAt && (
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.6rem" }}>
                    (edited)
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                {isOwner && (
                  <IconButton size="small" onClick={(e) => handleMenuOpen(e, each.id)} sx={{ p: 0.25 }}>
                    <MoreVert sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>

              {/* Content */}
              {editingId === each.id ? (
                <Box sx={{ ml: 3.5 }}>
                  <TextField
                    multiline
                    minRows={1}
                    maxRows={6}
                    fullWidth
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    variant="outlined"
                    size="small"
                    placeholder="Edit reply…"
                    autoFocus
                    sx={{ mb: 0.5 }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={handleEditCancel} sx={{ textTransform: "none", borderRadius: 2, fontSize: "0.7rem", py: 0.15 }}>
                      Cancel
                    </Button>
                    <Button size="small" variant="contained" onClick={handleEditSave} disabled={!editText.trim()} sx={{ textTransform: "none", borderRadius: 2, fontSize: "0.7rem", py: 0.15, background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)" }}>
                      Save
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Box sx={{ ml: 3.5 }}>
                  <RichContentViewer content={each.text} />
                </Box>
              )}
            </Box>
          );
        })}

        {/* Reply input */}
        <SendMessage ref={replyRef} handleSend={handleReply} />
        {/* Hidden trigger for parent Reply button */}
        <span id={replyTriggerId} style={{ display: "none" }} onClick={() => replyRef.current?.openTyping()} />

        {/* Context menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={() => handleEditStart(messages.find((m) => m.id === menuItemId))} dense>
            <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
          </MenuItem>
          <MenuItem onClick={() => handleDeleteClick(menuItemId)} dense sx={{ color: "error.main" }}>
            <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        </Menu>

        {/* Delete confirmation */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Reply</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this reply? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained" sx={{ textTransform: "none" }}>Delete</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }

  if (messages.length > 0) {
    return (
      <Box sx={{ mt: 0.5 }}>
        <Box
          onClick={accordionAction}
          sx={{ cursor: "pointer", display: "inline-flex" }}
        >
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`${messages.length} repl${messages.length > 1 ? "ies" : "y"}`}
            sx={{ fontWeight: 600, fontSize: "0.7rem", height: 22 }}
          />
        </Box>
        {/* Hidden trigger + ref for parent Reply button */}
        <SendMessage ref={replyRef} handleSend={handleReply} />
        <span id={replyTriggerId} style={{ display: "none" }} onClick={() => replyRef.current?.openTyping()} />
      </Box>
    );
  }

  return (
    <>
      <SendMessage ref={replyRef} handleSend={handleReply} />
      <span id={replyTriggerId} style={{ display: "none" }} onClick={() => replyRef.current?.openTyping()} />
    </>
  );
}
